import React, { useState } from 'react';
import { Checkbox, colors } from '@insurancemenu/imenu-components-lib';
import { Mail, Reload, Delete } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import BatchGuidelines from 'components/common/general/BatchGuidelines';
import BatchWarning from 'components/common/general/BatchWarning';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import {
  BatchButtonsStyled,
  BatchSectionStyled,
  ButtonStyled,
  CheckboxTextStyled,
  SelectedAllBlockStyled,
  SectionTitleStyled,
} from './styles';

export interface TriggerRenewalsBatchSectionProps {
  loading: boolean;
}

const TriggerRenewalsBatchSection = observer(({ loading }: TriggerRenewalsBatchSectionProps): JSX.Element => {
  const {
    triggerRenewalsStore: { isSelectAllButtonChecked, selectedPeriods, setSelectAllButtonStatus, openWarningModal },
    userStore: { userRoles },
  } = useRootStore();

  const [isGuidelineModalOpen, setIsGuidelineModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const convertedSelectedPeriods = selectedPeriods.map((periodId) => toJS(periodId)).slice();
  const convertedRoles = userRoles.map((role) => toJS(role)).slice();

  const areAllSelectedPeriodsWithExhibits = convertedSelectedPeriods.every((period) => {
    return period.installedProposal?.productLines.filter((line) => line.exhibitUrls.length === 0).length === 0;
  });
  const showGuidelineModal = convertedSelectedPeriods.length === 0;
  const showWarningModal = !areAllSelectedPeriodsWithExhibits && convertedSelectedPeriods.length > 0;
  const isCelticsRights = celticsRights(convertedRoles);

  const updateBatchModalState = (shouldWarningModalBeShown = true): void => {
    if (showGuidelineModal) {
      setIsWarningModalOpen(false);
      setIsGuidelineModalOpen(true);
    }
    if (showWarningModal && shouldWarningModalBeShown) {
      setIsGuidelineModalOpen(false);
      setIsWarningModalOpen(true);
    }
  };

  return (
    <BatchSectionStyled>
      <SectionTitleStyled>Batch Apply Section</SectionTitleStyled>
      <BatchButtonsStyled>
        <SelectedAllBlockStyled>
          <Checkbox
            label={<CheckboxTextStyled>Select All Per page</CheckboxTextStyled>}
            checked={isSelectAllButtonChecked}
            onChange={setSelectAllButtonStatus}
            highlightColor={colors.blue[600]}
            disabled={loading}
          />
        </SelectedAllBlockStyled>
        <ButtonStyled
          leftIconProp={<Mail />}
          type="text"
          color="primary"
          disabled={loading}
          onClick={(): void => {
            updateBatchModalState();
            if (areAllSelectedPeriodsWithExhibits && convertedSelectedPeriods.length !== 0) {
              openWarningModal('TRIGGER_SELECTED');
            }
          }}
        >
          Trigger
        </ButtonStyled>
        <BatchGuidelines
          show={isGuidelineModalOpen}
          alignment="center"
          onClose={(): void => {
            setIsGuidelineModalOpen(false);
          }}
          title="Select periods using checkboxes to utilize the batch feature."
        />
        <BatchWarning
          show={isWarningModalOpen}
          alignment="center"
          onClose={(): void => {
            setIsWarningModalOpen(false);
          }}
          title="Select periods with exhibits to use batch functionality."
        />
        {isCelticsRights && (
          <ButtonStyled
            leftIconProp={<Reload />}
            type="text"
            color="primary"
            disabled={loading}
            onClick={(): void => {
              updateBatchModalState();
              if (areAllSelectedPeriodsWithExhibits && convertedSelectedPeriods.length !== 0) {
                openWarningModal('MOVE_SELECTED_PERIODS_TO_RENEWAL');
              }
            }}
          >
            Move to renewal
          </ButtonStyled>
        )}
        <ButtonStyled
          leftIconProp={<Delete />}
          type="text"
          color="custom"
          customColor={colors.red[600]}
          disabled={loading}
          onClick={(): void => {
            updateBatchModalState(false);
            if (convertedSelectedPeriods.length !== 0) {
              openWarningModal('REMOVE_PERIODS');
            }
          }}
        >
          Remove Periods
        </ButtonStyled>
      </BatchButtonsStyled>
    </BatchSectionStyled>
  );
});

export default TriggerRenewalsBatchSection;
